/**
 * Live re-anchor a stored annotation to its current rect on the page.
 *
 * Every annotation carries `elementPath` (CSS selector) and `boundingBox`
 * (rect at capture time). After the page mutates — refactors, A/B
 * variants, the agent's own fix — the stored rect goes stale. This hook
 * tries to find the element again and returns the CURRENT rect.
 *
 *   - If the selector resolves and the element is still on the page →
 *     return the live rect + `drifted: false`.
 *   - If the selector resolves but the rect drifted significantly →
 *     return the live rect + `drifted: true`.
 *   - If the selector does NOT resolve → return the stored rect +
 *     `missing: true`. The marker shows a warning glyph.
 *
 * rAF-coalesced + ResizeObserver-driven so re-layouts update smoothly.
 */
import { useEffect, useState } from "react";
const DRIFT_THRESHOLD_PX = 24;
const stored = (s) => ({
    x: s.x,
    y: s.y,
    width: s.width,
    height: s.height,
    drifted: false,
    missing: false,
});
const missing = (s) => ({
    x: s.x,
    y: s.y,
    width: s.width,
    height: s.height,
    drifted: false,
    missing: true,
});
const isDrifted = (live, s) => {
    return (Math.abs(live.x - s.x) > DRIFT_THRESHOLD_PX ||
        Math.abs(live.y - s.y) > DRIFT_THRESHOLD_PX ||
        Math.abs(live.width - s.width) > DRIFT_THRESHOLD_PX ||
        Math.abs(live.height - s.height) > DRIFT_THRESHOLD_PX);
};
export const useLiveAnchor = (elementPath, storedBox) => {
    const [anchor, setAnchor] = useState(storedBox ? stored(storedBox) : null);
    useEffect(() => {
        if (!storedBox) {
            setAnchor(null);
            return;
        }
        let frame = 0;
        let observer = null;
        let observed = null;
        const measure = () => {
            let target = null;
            try {
                target = document.querySelector(elementPath);
            }
            catch {
                target = null;
            }
            if (!target) {
                setAnchor(missing(storedBox));
                return;
            }
            const r = target.getBoundingClientRect();
            if (r.width === 0 && r.height === 0) {
                setAnchor(missing(storedBox));
                return;
            }
            const live = {
                x: r.x,
                y: r.y,
                width: r.width,
                height: r.height,
                drifted: isDrifted(r, storedBox),
                missing: false,
            };
            setAnchor(live);
            // Wire a ResizeObserver to the resolved element so layout shifts
            // update the marker in real time.
            if (observed !== target) {
                observer?.disconnect();
                observer = new ResizeObserver(schedule);
                observer.observe(target);
                observed = target;
            }
        };
        const schedule = () => {
            if (frame !== 0)
                return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                measure();
            });
        };
        schedule();
        window.addEventListener("scroll", schedule, {
            passive: true,
            capture: true,
        });
        window.addEventListener("resize", schedule, { passive: true });
        // Re-measure when the DOM mutates anywhere — covers re-renders,
        // route changes, A/B variants — at the cost of one rAF per batch.
        const mut = new MutationObserver(schedule);
        mut.observe(document.body, {
            subtree: true,
            childList: true,
            attributes: true,
        });
        return () => {
            if (frame !== 0)
                cancelAnimationFrame(frame);
            window.removeEventListener("scroll", schedule, {
                capture: true,
            });
            window.removeEventListener("resize", schedule);
            mut.disconnect();
            observer?.disconnect();
        };
    }, [elementPath, storedBox?.x, storedBox?.y, storedBox?.width, storedBox?.height, storedBox]);
    return anchor;
};
//# sourceMappingURL=useLiveAnchor.js.map